<template>
  <el-drawer
    :visible="visible"
    :title="drawerTitle"
    direction="rtl"
    size="70%"
    append-to-body
    @update:visible="handleVisibleChange"
  >
    <div v-loading="loading" class="pos-id-detail px-3 pb-4">
      <b-alert v-if="readOnly" variant="warning" show>{{ readOnlyHint }}</b-alert>
      <b-alert v-if="errorText" variant="danger" show>{{ errorText }}</b-alert>

      <template v-if="detail">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h6 class="mb-0">基本資訊</h6>
          <div>
            <b-button
              v-if="!readOnly"
              size="sm"
              variant="outline-primary"
              @click="$emit('edit', detail)"
            >
              編輯
            </b-button>
            <b-button
              size="sm"
              variant="outline-secondary"
              class="ml-1"
              @click="handleVisibleChange(false)"
            >
              關閉
            </b-button>
          </div>
        </div>
        <pos-id-flags
          :record="detail"
          :siblings="siblingRecords"
          :special-ids="specialIds"
          class="mb-2"
        />
        <b-table
          bordered
          small
          striped
          :items="basicRows"
          :fields="itemFields"
          class="mb-3"
        />

        <h6 class="mb-1">已登記白名單（special_ids）</h6>
        <b-table
          bordered
          small
          striped
          show-empty
          empty-text="此 pos-id 未登記白名單"
          :items="specialIds"
          :fields="specialFields"
          class="mb-3"
        >
          <template #cell(film_id)="row">
            {{ textOrDash(row.item.film_id) }}
          </template>
          <template #cell(film_master_id)="row">
            {{ textOrDash(row.item.film_master_id) }}
          </template>
          <template #cell(title_name)="row">
            {{ textOrDash(row.item.title_name) }}
          </template>
          <template #cell(status)="row">
            <b-badge :variant="row.item.status === 'D' ? 'secondary' : 'success'">
              {{ specialStatusText(row.item.status) }}
            </b-badge>
          </template>
          <template #cell(remarks)="row">
            {{ textOrDash(row.item.remarks) }}
          </template>
        </b-table>

        <h6 class="mb-1">同一 pos-id 的其它匹配行（sibling_records）</h6>
        <b-table
          bordered
          small
          striped
          show-empty
          empty-text="同一院線、同一 pos-id 沒有其它匹配行"
          :items="siblingRecords"
          :fields="siblingFields"
          class="mb-3"
        >
          <template #cell(film_id)="row">
            {{ row.item.film_id }}
          </template>
          <template #cell(title_name)="row">
            <div>{{ row.item.title_name }}</div>
          </template>
          <template #cell(manual_linked)="row">
            {{ row.item.manual_linked ? '是' : '否' }}
          </template>
          <template #cell(actions)="row">
            <b-button size="sm" variant="outline-info" @click="$emit('open-record', row.item.id)">
              查看此筆
            </b-button>
          </template>
        </b-table>

        <h6 class="mb-1">用量摘要（usage_summary）</h6>
        <b-table
          v-if="usageRows.length > 0"
          bordered
          small
          striped
          :items="usageRows"
          :fields="itemFields"
          class="mb-3"
        />
        <p v-else class="text-muted">後端未回傳用量摘要。</p>

        <h6 class="mb-1">最近 10 條變更（recent_audits）</h6>
        <b-table
          bordered
          small
          striped
          show-empty
          empty-text="尚無變更記錄"
          :items="recentAudits"
          :fields="auditFields"
          class="mb-3"
        >
          <template #cell(change_type_label)="row">
            <b-badge :variant="changeTypeVariant(row.item)">
              {{ textOrDash(row.item.change_type_label) }}
            </b-badge>
          </template>
          <template #cell(source_label)="row">
            <b-badge :variant="sourceVariant(row.item)">
              {{ textOrDash(row.item.source_label) }}
            </b-badge>
            <b-badge v-if="row.item.source === 'auto_create'" variant="light" class="ml-1">
              系統
            </b-badge>
          </template>
          <template #cell(change)="row">
            <code class="small change-json">{{ changeText(row.item) }}</code>
          </template>
          <template #cell(reason)="row">
            {{ textOrDash(row.item.reason) }}
          </template>
          <template #cell(operator_name)="row">
            {{ textOrDash(row.item.operator_name) }}
          </template>
        </b-table>

        <h6 class="mb-1">該筆記錄的完整變更歷史</h6>
        <div v-loading="auditLoading">
          <b-table
            bordered
            small
            striped
            show-empty
            empty-text="尚無變更記錄"
            :items="audits"
            :fields="auditFields"
          >
            <template #cell(change_type_label)="row">
              <b-badge :variant="changeTypeVariant(row.item)">
                {{ textOrDash(row.item.change_type_label) }}
              </b-badge>
            </template>
            <template #cell(source_label)="row">
              <b-badge :variant="sourceVariant(row.item)">
                {{ textOrDash(row.item.source_label) }}
              </b-badge>
              <b-badge v-if="row.item.source === 'auto_create'" variant="light" class="ml-1">
                系統
              </b-badge>
            </template>
            <template #cell(change)="row">
              <code class="small change-json">{{ changeText(row.item) }}</code>
            </template>
            <template #cell(reason)="row">
              {{ textOrDash(row.item.reason) }}
            </template>
            <template #cell(operator_name)="row">
              {{ textOrDash(row.item.operator_name) }}
            </template>
          </b-table>
          <pos-id-pagination
            :page="auditPage"
            :count="auditCount"
            :total="auditsTotal"
            unit="條"
            :count-options="auditCountOptions"
            @page-change="onAuditPageChange"
            @count-change="onAuditCountChange"
          />
        </div>
      </template>

      <p v-else-if="!loading" class="text-muted">沒有可顯示的記錄。</p>
    </div>
  </el-drawer>
</template>

<script>
import { Message } from 'element-ui'
import {
  getFilmCircuitId,
  getFilmCircuitIdAudits
} from '@/api/posId'
import {
  auditChangeTypeVariant,
  auditSourceVariant,
  formatNumber,
  isUnmatchedRecord,
  POS_ID_READ_ONLY_HINT,
  resolvePosIdError
} from '@/utils/posId'
import PosIdFlags from './PosIdFlags.vue'
import PosIdPagination from './PosIdPagination.vue'

function hasText(value) {
  return value !== undefined && value !== null && String(value).trim() !== ''
}

function textOrDash(value) {
  return hasText(value) ? String(value) : '—'
}

function rangeText(from, to) {
  if (!hasText(from) && !hasText(to)) {
    return '不限（全部日期）'
  }
  const start = hasText(from) ? String(from) : '不限'
  const end = hasText(to) ? String(to) : '不限'
  return `${start} ~ ${end}`
}

/**
 * P2 詳情抽屜（guide §2.2 / §5 P2）：
 * 基本資訊 + PosIdFlags + special_ids + sibling_records + usage_summary + recent_audits，
 * 並用 getFilmCircuitIdAudits(id, {page,count}) 分頁顯示該筆記錄的完整變更歷史。
 */
export default {
  name: 'FilmCircuitIdDetailDrawer',
  components: {
    PosIdFlags,
    PosIdPagination
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    recordId: {
      type: [Number, String],
      default: null
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    // 列表端在編輯成功後 +1，用來強制重新載入詳情
    reloadToken: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      loading: false,
      auditLoading: false,
      errorText: '',
      detail: null,
      audits: [],
      auditsTotal: 0,
      auditPage: 1,
      auditCount: 10,
      auditCountOptions: [10, 20, 50, 100],
      readOnlyHint: POS_ID_READ_ONLY_HINT
    }
  },
  computed: {
    drawerTitle() {
      return this.detail ? `匹配記錄詳情 #${this.detail.id}` : '匹配記錄詳情'
    },
    specialIds() {
      const list = this.detail ? this.detail.special_ids : null
      return Array.isArray(list) ? list : []
    },
    siblingRecords() {
      const list = this.detail ? this.detail.sibling_records : null
      return Array.isArray(list) ? list : []
    },
    recentAudits() {
      const list = this.detail ? this.detail.recent_audits : null
      return Array.isArray(list) ? list : []
    },
    usageSummary() {
      const usage = this.detail ? this.detail.usage_summary : null
      return usage && typeof usage === 'object' ? usage : null
    },
    basicRows() {
      const row = this.detail
      if (!row) {
        return []
      }
      const rows = [
        { item: '記錄 ID', value: String(row.id) },
        { item: '院綫 Circuit', value: `${textOrDash(row.circuit_name)}（${textOrDash(row.circuit_id)}）` },
        { item: 'pos-id', value: textOrDash(row.pos_id) },
        { item: 'Film ID', value: isUnmatchedRecord(row) ? `${row.film_id}（未匹配）` : textOrDash(row.film_id) },
        { item: 'Master ID', value: textOrDash(row.master_id) },
        { item: '電影標題 Title Name', value: textOrDash(row.title_name) },
        { item: '狀態 Status', value: hasText(row.status) ? String(row.status) : '有效' },
        { item: '曾手動連接 Manual Linked', value: row.manual_linked ? '是' : '否' },
        { item: '備注 Remarks', value: textOrDash(row.remarks) },
        { item: '創建時間 Created At', value: textOrDash(row.created_at) },
        { item: '更新時間 Updated At', value: textOrDash(row.updated_at) }
      ]
      if (row.title_source !== undefined && row.title_source !== null) {
        rows.splice(6, 0, { item: '標題來源', value: textOrDash(row.title_source) })
      }
      return rows
    },
    usageRows() {
      const usage = this.usageSummary
      if (!usage) {
        return []
      }
      return [
        { item: '日結場次（finals_count）', value: formatNumber(usage.finals_count) },
        { item: '日結金額（finals_gross）', value: formatNumber(usage.finals_gross) },
        { item: '日結人次（finals_admissions）', value: formatNumber(usage.finals_admissions) },
        { item: '每小時場次（hourlies_count）', value: formatNumber(usage.hourlies_count) },
        { item: '每小時金額（hourlies_gross）', value: formatNumber(usage.hourlies_gross) },
        { item: '每小時人次（hourlies_admissions）', value: formatNumber(usage.hourlies_admissions) },
        { item: '日期範圍', value: rangeText(usage.date_from, usage.date_to) }
      ]
    },
    itemFields() {
      return [
        { key: 'item', label: 'Title', thStyle: { width: '250px' } },
        { key: 'value', label: 'Value' }
      ]
    },
    specialFields() {
      return [
        { key: 'id', label: '白名單 id', thStyle: { width: '90px' } },
        { key: 'film_id', label: 'Film ID', thStyle: { width: '90px' } },
        { key: 'film_master_id', label: 'Master id', thStyle: { width: '90px' } },
        { key: 'title_name', label: '標題' },
        { key: 'status', label: '狀態', thStyle: { width: '90px' } },
        { key: 'remarks', label: '備註', thStyle: { width: '130px' } }
      ]
    },
    siblingFields() {
      return [
        { key: 'id', label: '記錄 ID', thStyle: { width: '90px' } },
        { key: 'film_id', label: 'Film ID', thStyle: { width: '90px' } },
        { key: 'master_id', label: 'Master id', thStyle: { width: '90px' } },
        { key: 'title_name', label: '電影標題 Title Name' },
        { key: 'status', label: '狀態', thStyle: { width: '80px' } },
        { key: 'manual_linked', label: '鎖定', thStyle: { width: '70px' } },
        { key: 'actions', label: '操作', thStyle: { width: '100px' } }
      ]
    },
    auditFields() {
      return [
        { key: 'created_at', label: '時間', thStyle: { width: '150px' } },
        { key: 'action_label', label: '匹配動作', thStyle: { width: '90px' } },
        { key: 'source_label', label: '創建來源', thStyle: { width: '100px' } },
        { key: 'change_type_label', label: '變動類型', thStyle: { width: '80px' } },
        { key: 'change', label: '變更內容' },
        { key: 'reason', label: '緣由', thStyle: { width: '120px' } },
        { key: 'operator_name', label: '操作人', thStyle: { width: '100px' } }
      ]
    }
  },
  watch: {
    visible(value) {
      if (value === true) {
        this.loadDetail()
      }
    },
    recordId() {
      if (this.visible === true) {
        this.loadDetail()
      }
    },
    reloadToken() {
      if (this.visible === true) {
        this.loadDetail()
      }
    }
  },
  methods: {
    textOrDash,
    handleVisibleChange(value) {
      this.$emit('update:visible', value === true)
    },
    specialStatusText(status) {
      if (status === 'D') {
        return '已停用'
      }
      return hasText(status) ? String(status) : '有效'
    },
    changeTypeVariant(row) {
      return auditChangeTypeVariant(row ? row.change_type : '')
    },
    sourceVariant(row) {
      return auditSourceVariant(row ? row.source : '')
    },
    changeText(row) {
      const before = row && row.before ? JSON.stringify(row.before) : '（無）'
      const after = row && row.after ? JSON.stringify(row.after) : '（無）'
      return `${before} → ${after}`
    },
    loadDetail() {
      if (!hasText(this.recordId)) {
        return
      }
      this.loading = true
      this.errorText = ''
      getFilmCircuitId(this.recordId)
        .then((res) => {
          this.detail = res && res.data ? res.data : null
          this.loading = false
          this.auditPage = 1
          this.loadAudits()
        })
        .catch((error) => {
          this.loading = false
          this.detail = null
          const info = resolvePosIdError(error)
          this.errorText = info.message
          Message.error(info.message)
          if (info.isPermissionDenied) {
            this.$emit('read-only')
          }
          if (info.code === 10010) {
            this.$emit('missing')
          }
        })
    },
    loadAudits() {
      if (!hasText(this.recordId)) {
        return
      }
      this.auditLoading = true
      getFilmCircuitIdAudits(this.recordId, { page: this.auditPage, count: this.auditCount })
        .then((res) => {
          this.audits = res && Array.isArray(res.data) ? res.data : []
          this.auditsTotal = res && res.total !== undefined ? Number(res.total) : 0
          this.auditLoading = false
        })
        .catch((error) => {
          this.auditLoading = false
          const info = resolvePosIdError(error)
          Message.error(info.message)
        })
    },
    onAuditPageChange(page) {
      this.auditPage = Number(page) || 1
      this.loadAudits()
    },
    onAuditCountChange(count) {
      this.auditCount = Number(count) || 10
      this.auditPage = 1
      this.loadAudits()
    }
  }
}
</script>

<style lang="scss" scoped>
.pos-id-detail {
  h6 {
    color: #0e72bc;
    font-weight: 500;
  }
  code {
    word-break: break-all;
  }
  ::v-deep table {
    table-layout: fixed;
  }
  ::v-deep td,
  ::v-deep th {
    white-space: normal;
    word-break: break-word;
    vertical-align: top;
  }
  ::v-deep .change-json {
    white-space: pre-wrap;
    word-break: break-all;
  }
}
</style>
